import * as React from "react";
import {PluginTemplates} from "../../../../utils/PluginTemplates";
import FalconHeader from "../../../../components/navigation/FalconHeader";
import {RouteComponentProps} from "react-router";
import {
    ICustomerPortalService,
    INTERACTION_PROCESS_MTA_CHANGE_PAYMENT_DAY_RENEWAL_PROPOSAL,
    Renewal,
    Types
} from "@falcon/services";
import {lazyInject} from "@athena-portal/core";
import {authorize, Overlay} from "@athena-portal/components";
import NotificationModal from "../../../../components/modals/NotificationModal";
import FalconFooter from "../../../../components/navigation/FalconFooter";
import DirectDebitPaymentSchedule from "../../../../components/widgets/DirectDebitPaymentSchedule";

interface IMatchParams {
    policyNumber: string;
}

interface IProps extends RouteComponentProps<IMatchParams> {
}

interface IState {
    newPaymentDay: number;
    isOpen: boolean;
    showNoDaySelectedAlert: boolean;
    showOverlay: boolean;
    proposalLoaded: boolean;
    paymentDayOptions: any[];
    renewalProposal: any;
}

@authorize
export class DirectDebitRenewalChangePaymentDayPage extends React.Component<IProps, IState> {

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    private pluginTemplates = new PluginTemplates();

    constructor(props: IProps) {
        super(props);

        this.state = {
            newPaymentDay: 0,
            isOpen: false,
            showNoDaySelectedAlert: false,
            showOverlay: false,
            paymentDayOptions: [],
            proposalLoaded: false,
            renewalProposal: undefined,
        }

        this.updatePaymentDay = this.updatePaymentDay.bind(this);
        this.saveAndConfirm = this.saveAndConfirm.bind(this);
        this.cancelChanges = this.cancelChanges.bind(this);
        this.closeNoPaymentDaySelectedModal = this.closeNoPaymentDaySelectedModal.bind(this)
        this.renderAlert = this.renderAlert.bind(this)
    }

    public componentDidMount(): void {
        this.pluginTemplates.setPageTitle("Change your payment day");
        this.fetchAvailablePaymentDays();

        this.customerPortalService.getRenewalProposal(this.props.match.params.policyNumber)
            .subscribe((renewalProposal: Renewal) => {
                this.setState({
                    renewalProposal,
                    proposalLoaded: true,
                })
            })

    }

    public render(): React.ReactNode {
        const previousPage = `/dashboard/annualPaymentHistory/${this.props.match.params.policyNumber}`;
        if (this.state.proposalLoaded) {
            return (
                <>
                    <FalconHeader/>
                    <div className="plugin">
                        {this.pluginTemplates.renderBackReloadLink("Back", true, previousPage)}
                        {this.renderPageTitle()}
                        {this.renderHeadsUpPanel()}
                        {this.renderChangePaymentDatePanel()}
                        {this.renderNavigationButtons()}
                        {this.pluginTemplates.renderBackReloadLink("Back", false, previousPage)}
                    </div>
                    <FalconFooter/>
                    <Overlay show={this.state.showOverlay}/>
                    {this.renderAlert()}
                </>
            );
        } else {
           return (<></>)
        }
    }

    private fetchAvailablePaymentDays(): void {
        const options: string[] = ["1st", "2nd", "3rd"];
        for (let i = 4; i <= 28; i++) {
            options.push(`${i}th`);
        }
        this.setState({paymentDayOptions: options});
    }

    private renderPageTitle(): React.ReactNode {
        return (
            <div className="text-group title plugin no-indent-desktop">
                <h1>Change your payment day</h1>
            </div>
        );
    }

    private renderHeadsUpPanel(): React.ReactNode {
        return (
            <p className="_panel py-md-3 banner banner-info">
                Heads up - your change will take effect in a future month and won’t apply to your next
                payment. Please click ‘View instalments plan’ below to check your schedule as this could result in two payments close together.
            </p>
        );
    }

    private renderChangePaymentDatePanel(): React.ReactNode {
        return (
            <div className="panel">
                <div className="form-group">
                    <label htmlFor="changePaymentDayQuestion">Which day of the month should we take your
                        payment?</label>
                    <div className="input-group">
                        <select id="changePaymentDayQuestion" className="form-control"
                                onChange={this.updatePaymentDay} value={this.state.newPaymentDay}
                                disabled={!this.state.paymentDayOptions}>
                            {this.renderPaymentDayOptions()}
                        </select>
                    </div>
                </div>
                <div className="dashed-10"/>
                <label htmlFor="paymentScheduleTable" className="text-center mt-3">
                    <div className="direct-debit-panel-title mb-2">Payment schedule</div>
                </label>
                <div id="paymentScheduleTable" className="mb-3">
                    <DirectDebitPaymentSchedule
                        isOpen={this.state.isOpen}
                        instalments={this.state.renewalProposal.paymentDetails.instalmentPlan.instalments}
                        interaction={this.state.renewalProposal}
                        instalmentDueDates={true}/>
                </div>
                <div className="text-center">
                    {!this.state?.isOpen ?
                        <button
                            className="btn-inner-link btn-text text-center cursor-pointer text-decoration-none mb-5"
                            onClick={this.toggleInstallmentPlan}>
                            + <span className="text-decoration-underline">View instalments plan</span>
                        </button>
                        :
                        <button
                            className="btn-inner-link btn-text text-center cursor-pointer text-decoration-none mb-5"
                            onClick={this.toggleInstallmentPlan}>
                            - <span className="text-decoration-underline">Hide instalments plan</span>
                        </button>
                    }
                </div>
            </div>
        );
    }

    private toggleInstallmentPlan = (): void => {
        this.setState((prevState: Readonly<IState>) => ({
            isOpen: !prevState.isOpen
        }))
    }

    private renderPaymentDayOptions(): React.ReactNode {
        return (
            <>
                <option value={0}>Please select...</option>
                {this.state.paymentDayOptions.map((option: string, index: number) => {
                    return (
                        <option key={option} className={option} value={index + 1}>{option}</option>
                    )
                })}
            </>
        );
    }

    private renderNavigationButtons(): React.ReactNode {
        return (
            <>
                <div className="form-group">
                    <button type="button" className="btn btn-default btn-white-dashboard margin-y40"
                            onClick={this.cancelChanges}>
                        Cancel
                    </button>
                    <button type="button" className="btn btn-default btn-primary float-right"
                            onClick={this.saveAndConfirm}
                            disabled={!this.state.paymentDayOptions || this.state.paymentDayOptions?.length === 0}>
                        Save and confirm
                    </button>
                </div>
            </>
        );
    }

    private updatePaymentDay(event: any): void {
        const selectedDate: number = parseInt(event.target.value, 10);

        this.setState({newPaymentDay: selectedDate}, () => this.updatePreviewInstalmentPlan())
    }

    private updatePreviewInstalmentPlan(): void {
        let { renewalProposal } = this.state;
        const instalments = renewalProposal.paymentDetails.instalmentPlan.instalments.map((instalment: any, index: number) => {
            if (index === 0) {
                return instalment;
            }
            const date = new Date(instalment.dueDate);
            date.setDate(this.state.newPaymentDay);
            instalment.dueDate = date.toISOString();
            return instalment;
        });
        renewalProposal.paymentDetails.instalmentPlan.instalments = instalments;
        this.setState({renewalProposal});

    }

    private saveAndConfirm(): void {
        const allowedPaymentDay: boolean = this.state.newPaymentDay !== 0;

        this.setState({
            showOverlay: true
        });

        if (allowedPaymentDay) {
            this.customerPortalService.storeAnnualToMonthlyPaymentDay(this.props.match.params.policyNumber, this.state.newPaymentDay).subscribe(() => {
                window.sessionStorage.setItem("showPopup", "true");
                window.sessionStorage.setItem("referrerJourney", INTERACTION_PROCESS_MTA_CHANGE_PAYMENT_DAY_RENEWAL_PROPOSAL);
                this.setState({
                    showNoDaySelectedAlert: false
                })

                this.props.history.push(`/dashboard/annualPaymentHistory/${this.props.match.params.policyNumber}`);
            });
        } else {
            this.setState({
                showNoDaySelectedAlert: true,
                showOverlay: false
            })
        }
    }

    private cancelChanges(): void {
        this.props.history.push(`/dashboard/annualPaymentHistory/${this.props.match.params.policyNumber}`);
    }

    private renderAlert(): React.ReactNode {
        const wording: string = "Please select a payment date to continue.";

        return (
            <NotificationModal show={this.state.showNoDaySelectedAlert} hide={this.closeNoPaymentDaySelectedModal}>
                {wording}
            </NotificationModal>
        )
    }

    private closeNoPaymentDaySelectedModal(): void {
        this.setState((prevState: Readonly<IState>) => ({
            showNoDaySelectedAlert: !prevState.showNoDaySelectedAlert
        }))
    }
}



// WEBPACK FOOTER //
// ./src/pages/dashboard/common/payments/DirectDebitRenewalChangePaymentDayPage.tsx